import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Activity, BarChart3, AlertCircle, Loader } from 'lucide-react';
import PersonalBestSection from './PersonalBestSection';
import EliteComparisonSection from './EliteComparisonSection';
import ConsistencySection from './ConsistencySection';
import ProgressChartSection from './ProgressChartSection';
import { API_URL } from './config/api';
import authService from './services/authService';
import './ComparisonPanel.css';

const ComparisonPanel = ({ videoId, elementType }) => {
  // State for each section's data
  const [personalBest, setPersonalBest] = useState(null);
  const [eliteComparison, setEliteComparison] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [progressData, setProgressData] = useState(null);

  // Loading states for each section
  const [loadingPersonalBest, setLoadingPersonalBest] = useState(true);
  const [loadingElite, setLoadingElite] = useState(true);
  const [loadingConsistency, setLoadingConsistency] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(true);

  // Error states
  const [errorPersonalBest, setErrorPersonalBest] = useState(null);
  const [errorElite, setErrorElite] = useState(null);
  const [errorConsistency, setErrorConsistency] = useState(null);
  const [errorProgress, setErrorProgress] = useState(null);

  // Fetch all data when videoId or elementType changes
  useEffect(() => {
    if (videoId && elementType) {
      fetchAllData();
    }
  }, [videoId, elementType]);

  const fetchAllData = async () => {
    // Reset states
    setLoadingPersonalBest(true);
    setLoadingElite(true);
    setLoadingConsistency(true);
    setLoadingProgress(true);
    setErrorPersonalBest(null);
    setErrorElite(null);
    setErrorConsistency(null);
    setErrorProgress(null);

    // Fetch personal best comparison
    fetchPersonalBest();

    // Fetch elite standards comparison
    fetchEliteComparison();

    // Fetch consistency analysis
    fetchConsistency();

    // Fetch progress chart data
    fetchProgressData();
  };

  const fetchPersonalBest = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comparison/personal-best/${videoId}?element_type=${elementType}`,
        {
          headers: {
            ...authService.getAuthHeader(),
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setPersonalBest(data.data);
        setErrorPersonalBest(null);
      } else {
        setErrorPersonalBest(data.error || 'Failed to load personal best comparison');
      }
    } catch (error) {
      console.error('Error fetching personal best:', error);
      setErrorPersonalBest('Network error loading personal best comparison');
    } finally {
      setLoadingPersonalBest(false);
    }
  };

  const fetchEliteComparison = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comparison/elite-standards/${videoId}?element_type=${elementType}&target_level=olympic_podium`,
        {
          headers: {
            ...authService.getAuthHeader(),
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setEliteComparison(data.data);
        setErrorElite(null);
      } else {
        setErrorElite(data.error || 'Failed to load elite comparison');
      }
    } catch (error) {
      console.error('Error fetching elite comparison:', error);
      setErrorElite('Network error loading elite comparison');
    } finally {
      setLoadingElite(false);
    }
  };

  const fetchConsistency = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/consistency/${elementType}?days=30`,
        {
          headers: {
            ...authService.getAuthHeader(),
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setConsistency(data.data);
        setErrorConsistency(null);
      } else {
        setErrorConsistency(data.error || 'Failed to load consistency analysis');
      }
    } catch (error) {
      console.error('Error fetching consistency:', error);
      setErrorConsistency('Network error loading consistency analysis');
    } finally {
      setLoadingConsistency(false);
    }
  };

  const fetchProgressData = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/progress-chart/${elementType}?days=90&metrics=jump_height_cm,rotation_speed_rev_s,isu_score`,
        {
          headers: {
            ...authService.getAuthHeader(),
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setProgressData(data.data);
        setErrorProgress(null);
      } else {
        setErrorProgress(data.error || 'Failed to load progress data');
      }
    } catch (error) {
      console.error('Error fetching progress data:', error);
      setErrorProgress('Network error loading progress data');
    } finally {
      setLoadingProgress(false);
    }
  };

  // Format element type for display
  const formatElementType = (type) => {
    return type
      .replace('jump_', '')
      .replace('spin_', '')
      .replace('combo_', 'Combo: ')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="comparison-panel">
      <div className="comparison-header">
        <div className="header-content">
          <BarChart3 className="header-icon" />
          <div>
            <h3 className="header-title">Performance Analysis</h3>
            <p className="header-subtitle">
              {formatElementType(elementType)} - Comparing to your history and elite standards
            </p>
          </div>
        </div>
      </div>

      <div className="comparison-sections">
        {/* Personal Best Section */}
        <div className="section-card">
          <div className="section-header">
            <TrendingUp className="section-icon" />
            <h4 className="section-title">Personal Best Comparison</h4>
          </div>
          <PersonalBestSection
            data={personalBest}
            loading={loadingPersonalBest}
            error={errorPersonalBest}
          />
        </div>

        {/* Elite Standards Section */}
        <div className="section-card">
          <div className="section-header">
            <Target className="section-icon" />
            <h4 className="section-title">Elite Standards Comparison</h4>
          </div>
          <EliteComparisonSection
            data={eliteComparison}
            loading={loadingElite}
            error={errorElite}
          />
        </div>

        {/* Consistency Section */}
        <div className="section-card">
          <div className="section-header">
            <Activity className="section-icon" />
            <h4 className="section-title">Consistency Analysis</h4>
          </div>
          <ConsistencySection
            data={consistency}
            loading={loadingConsistency}
            error={errorConsistency}
          />
        </div>

        {/* Progress Chart Section */}
        <div className="section-card section-card-wide">
          <div className="section-header">
            <BarChart3 className="section-icon" />
            <h4 className="section-title">Progress Over Time</h4>
          </div>
          <ProgressChartSection
            data={progressData}
            loading={loadingProgress}
            error={errorProgress}
            elementType={elementType}
          />
        </div>
      </div>
    </div>
  );
};

export default ComparisonPanel;
